/**
 * Map technical / Firebase permission failures to human-readable copy.
 * Never surface "Missing or insufficient permissions", redeploy hints, or vendor jargon.
 */

const PERMISSION_RE =
  /missing or insufficient permissions|permission[-_ ]denied|PERMISSION_DENIED/i;
const TECH_RE = /firestore|firebase|redeploy|npx firebase|cloud firestore/i;

/**
 * @param {"home"|"profile"|"tournament"|"organization"|"organizer"|"arena"|"default"} [context]
 */
export function permissionUserMessage(context = "default") {
  switch (context) {
    case "home":
      return "We couldn't load your home. Sign in with the right account, or try again.";
    case "profile":
      return "We couldn't load your profile. Try again in a moment.";
    case "tournament":
      return "You don't have access to this tournament.";
    case "organization":
      return "This organization hasn't granted you permission.";
    case "organizer":
      return "This action requires organizer access.";
    case "arena":
      return "You don't have access to this arena. Ask the host if you need permission.";
    default:
      return "You don't have access to this.";
  }
}

/**
 * @param {unknown} err
 * @param {{ fallback?: string; context?: string }} [options]
 * @returns {string}
 */
export function toUserFacingErrorMessage(err, options = {}) {
  const fallback = options.fallback || "Something went wrong. Try again.";
  const context = options.context || "default";
  const code =
    err && typeof err === "object" && "code" in err ? String(err.code || "") : "";
  const message =
    err instanceof Error
      ? err.message
      : err && typeof err === "object" && "message" in err
        ? String(err.message || "")
        : typeof err === "string"
          ? err
          : "";

  if (
    code === "permission-denied" ||
    code === "PERMISSION_DENIED" ||
    PERMISSION_RE.test(message) ||
    TECH_RE.test(message)
  ) {
    return permissionUserMessage(context);
  }

  if (!message.trim()) return fallback;
  if (TECH_RE.test(message) || PERMISSION_RE.test(message)) {
    return permissionUserMessage(context);
  }
  return message;
}

/**
 * @param {unknown} err
 */
export function isPermissionStyleError(err) {
  const code =
    err && typeof err === "object" && "code" in err ? String(err.code || "") : "";
  const message =
    err instanceof Error
      ? err.message
      : err && typeof err === "object" && "message" in err
        ? String(err.message || "")
        : typeof err === "string"
          ? err
          : "";
  return (
    code === "permission-denied" ||
    code === "PERMISSION_DENIED" ||
    PERMISSION_RE.test(message)
  );
}
